import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { reverse, sortBy, toLower } from "lodash"
import moment from "moment-timezone"
import GlobalHelper from "../../common/GlobalHelper"
import {
    bundlesListFields,
    bundlesListTotals,
    getBundlesFilters
} from "../../common/search-filter/search/bundleFilters/bundlesFilters"
import {
    calendarEventFields,
    calendarEventListTotals,
    getCalendarEventFilters
} from "../../common/search-filter/search/calendarEventFilters.jsx"
import {
    companyListFields,
    companyListTotals,
    getCompanyFilters
} from "../../common/search-filter/search/companyFilters/companyFilters"
import {
    contactFields,
    contactTotals,
    getContactsFilters
} from "../../common/search-filter/search/contactFilters/contactsFilters"
import {
    creditMemoSearchFields,
    creditMemoTotals,
    getCreditMemosFilters
} from "../../common/search-filter/search/creditMemosFilters/creditMemosFilters"
import {
    externalOrderFields,
    externalOrderTotals,
    getExternalOrderFilters
} from "../../common/search-filter/search/externalOrderFilters"
import {
    getInvoicesFilters, invoiceFields,
    invoiceTotals
} from "../../common/search-filter/search/invoiceFilters.jsx"
import {
    getItemReportFilters,
    itemReportFields,
    itemReportListTotals
} from "../../common/search-filter/search/itemreportfilters.jsx"
import { getMessagesFilters, messageFields, messageTotals } from "../../common/search-filter/search/messagesFilters.jsx"
import {
    customDateFilterMatchId,
    customDatePath,
    getOrdersFilters,
    orderFields,
    orderTotals
} from "../../common/search-filter/search/ordersFilters.jsx"
import {
    getPaymentsFilters,
    paymentsListFields,
    paymentsListTotals
} from "../../common/search-filter/search/paymentFilters/paymentsFilters"
import {
    getPricingListFilters,
    pricingListFields,
    pricingListTotals
} from "../../common/search-filter/search/pricingListFilters.jsx"
import { getProductFields, getProductsFilters, productTotals } from "../../common/search-filter/search/productsFilters.jsx"
import { getQuoteFilters, quoteFields, quoteTotals } from "../../common/search-filter/search/quotesFilters.jsx"
import { getReturnFilters, returnFields, returnTotals } from "../../common/search-filter/search/returnFilters.jsx"
import {
    getSchedulingFilters,
    schedulingFields,
    schedulingListTotals
} from "../../common/search-filter/search/schedulingFilters.jsx"
import {
    getStockDocumentsFilters, stockDocumentsListFields,
    stockDocumentsListTotals
} from "../../common/search-filter/search/stockDocumentsFilters/stockDocumentsFilters"
import {
    getStockImportsFilters, stockImportsListFields,
    stockImportsListTotals
} from "../../common/search-filter/search/stockimportFilters/stockDocumentsFilters"
import {
    getSubscriptionFilters,
    subscriptionFields,
    subscriptionTotals
} from "../../common/search-filter/search/subscriptionFilters.jsx"
import { getSuppliersFilters, supplierFields } from "../../common/search-filter/search/supplierFilters/suppliersFilters"
import { Settings, SupportedSettings } from "../../settings/account/Settings"
import { HttpError } from "../helpers/Error"
import HttpClient from "../helpers/HttpClient"
import SearchResults, { SearchResult, SearchResultDto } from "./Search"
import { SearchPayload } from "./SearchPayload"
import SearchTotals, { SearchTotalsDto } from "./SearchTotals"

export type SearchData = {
    keywords: string
    objects: string[]
    fields: string[]
    totals: string[]
    filters: { [key: string]: string | boolean | object | string[]; }
    lightResult: boolean
}

type Properties = {
    fields: string[],
    totals: string[],
    filters: object[],
}

class SearchService {
    static httpClient: HttpClient<SearchResults,
        SearchResultDto> = new HttpClient<SearchResults, SearchResultDto>(SearchResults)
    static searchTotalsHttpClient: HttpClient<SearchTotals,
        SearchTotalsDto> = new HttpClient<SearchTotals, SearchTotalsDto>(SearchTotals)

    static async listingSearch(searchPayload: SearchPayload): Promise<SearchResults | HttpError> {
        return this.httpClient.post("/listing/search", searchPayload)
    }

    static async productListingSearchWithPriceLevels(searchPayload: SearchPayload): Promise<SearchResults | HttpError> {
        return this.httpClient.post("/listing/products-with-prices/search", searchPayload)
    }

    static async pricelistListingSearch(searchPayload: SearchPayload): Promise<SearchResults | HttpError> {
        return this.httpClient.post("/listing/pricelists/invoices-and-returns/search", searchPayload)
    }

    static async itemReportListingSearch(searchPayload: SearchPayload): Promise<SearchResults | HttpError> {

        const res = await AppContext.getApiClient().post("/reports/item-reports", searchPayload)

        const result = Array.isArray(res) ? res.map(({ attributes, type }) => ({
            ...attributes,
            type,
        })) : []

        //lodash sortBy sorts in ascending order by default
        const resultSortedAsc = sortBy(result, [function(o) {
            const value = o[searchPayload.sortByField]
            return typeof value === "string" ? toLower(value) : value
        }])

        return {
            data: searchPayload.sortAsc ? resultSortedAsc : reverse(resultSortedAsc),
            metadata: {
                activePage: 1,
                pageSize: 50,
                pageSizeMax: 50,
                totalItemsCount: result.length
            }
        } as unknown as SearchResults
    }

    static async mainSearch<dataT>(payload: SearchPayload): Promise<SearchResult<dataT>> {
        return AppContext.getApiClient().post("/listing/search", payload)
    }

    static async mainSearchTotals(payload: object) {
        return this.searchTotalsHttpClient.post("/listing/search-totals", payload)
    }

    static getCustomerIds = async (keyword, activePage, pageSize, labels): Promise<string[]> => {
        const res = await AppContext.getApiClient().post("/listing/search", getContactSearchPostData(keyword,
            activePage,
            pageSize,
            labels
        ))
        return res.data.map(c => c.id)
    }

    static getCustomers = async (keyword, activePage, pageSize, labels) => {
        return await AppContext.getApiClient().post("/listing/search", getContactSearchPostData(
            keyword,
            activePage,
            pageSize,
            labels
        ))
    }

    static getTotalsFiltersAndFields = (props) => {
        let properties: Properties = { "fields": [], "totals": [], "filters": [] }
        switch (props.typeOfSearch) {
        case "invoices":
            properties = { "fields": invoiceFields, "totals": invoiceTotals, "filters": getInvoicesFilters(props) }
            break
        case "inventory-import-product":
            properties = { "fields": stockImportsListFields, "totals": stockImportsListTotals, "filters": getStockImportsFilters(props)}
            break
        case "credit-memos":
            properties = { "fields": creditMemoSearchFields, "totals": creditMemoTotals, "filters": getCreditMemosFilters(props) }
            break
        case "orders":
            properties = { "fields": orderFields, "totals": orderTotals, "filters": getOrdersFilters(props) }
            break
        case "inventory-adjustment":
            properties = { "fields": [], "totals": [], "filters": [] }
            break
        case "inventory-stock-documents":
            properties = { "fields": stockDocumentsListFields, "totals": stockDocumentsListTotals, "filters": getStockDocumentsFilters(props) }
            break
        case "quotes":
            properties = { "fields": quoteFields, "totals": quoteTotals, "filters": getQuoteFilters(props) }
            break
        case "subscription":
            properties = { "fields": subscriptionFields, "totals": subscriptionTotals, "filters": getSubscriptionFilters(props) }
            break
        case "priceList":
            properties = { "fields": pricingListFields, "totals": pricingListTotals, "filters": getPricingListFilters(props) }
            break
        case "return":
            properties = { "fields": returnFields, "totals": returnTotals, "filters": getReturnFilters(props) }
            break
        case "schedulingList":
        case "scheduling-pages":
            properties = { "fields": schedulingFields, "totals": schedulingListTotals, "filters": getSchedulingFilters(props) }
            break
        case "calendar-events":
        case "calendarEventList":
            properties = { "fields": calendarEventFields,
                "totals": calendarEventListTotals,
                "filters": getCalendarEventFilters(props)
            }
            break
        case "itemReportList":
            properties = { "fields": itemReportFields, "totals": itemReportListTotals, "filters": getItemReportFilters(props) }
            break
        case "products":
            properties = { "fields": getProductFields(Settings, SupportedSettings),
                "totals": productTotals,
                "filters": getProductsFilters(props, true)
            }
            break
        case "matrices":
            properties = { "fields": getProductFields(Settings, SupportedSettings),
                "totals": productTotals,
                "filters": getProductsFilters(props)
            }
            break
        case "contacts":
            properties = { "fields": contactFields, "totals": contactTotals, "filters": getContactsFilters(props) }
            break
        case "suppliers":
            properties = { "fields": supplierFields, "totals": [], "filters": getSuppliersFilters(props) }
            break
        case "companies":
            properties = {
                "fields": companyListFields,
                "totals": companyListTotals,
                "filters": getCompanyFilters(props)
            }
            break
        case "messages":
            properties = { "fields": messageFields, "totals": messageTotals, "filters": getMessagesFilters(props) }
            break
        case "externalOrders":
            properties = { "fields": externalOrderFields,
                "totals": externalOrderTotals,
                "filters": getExternalOrderFilters(props)
            }
            break
        case "payments":
            properties = {
                "fields": paymentsListFields,
                "totals": paymentsListTotals,
                "filters": getPaymentsFilters(props)
            }
            break
        case "bundles":
            properties = {
                "fields": bundlesListFields,
                "totals": bundlesListTotals,
                "filters": getBundlesFilters(props)
            }
            break
        }
        return properties
    }

    static getTotals = (objectType : string ) => {
        let totals : string[]
        switch (objectType) {
        case "invoices":
            totals = invoiceTotals
            return totals
        }
        return []
    }

    static async findContactDuplicates(fields, value) {
        const postData = {
            keywords: value,
            objects: ["contacts"],
            fields: [fields],
            pageSize: 5,
            activePage: 1,
            sortByField: "createdAt",
            sortAsc: true,
            lightResult: true,
            filters: {},
        }
        if (typeof value != "string") {
            postData.keywords = "*"
            // @ts-ignore
            postData.filters[[fields]] = value
        }
        return this.httpClient.post("/listing/search", postData)
    }

    static getSearchPostData(filtersToUse, typeOfSearch, filtersApplied, productType, object, totals, fields, searchKeywords): SearchData {
        const filters = SearchService.getDefaultFilters(typeOfSearch)
        let filtersAux = {}

        Object.keys(filtersToUse || {}).forEach(filter => {
            const elem = filtersApplied?.find(ft => ft.nameId === filter)
            const op = (elem && filtersToUse[elem.nameId]) ? filtersToUse[elem.nameId].op : ""
            filtersAux = {}
            if (elem !== undefined && elem.elementSubtype === undefined) {
                if (elem.type === "select") {
                    if (elem.subtype === "nested" && op === "isNotOneOf") {
                        filtersAux = SearchService.evaluateSelectFilter(elem, filters, elem.filterMatchNotOneOf, filtersToUse)
                    } else {
                        filtersAux = SearchService.evaluateSelectFilter(elem, filters, elem.filterMatch, filtersToUse)
                    }
                } else if (elem.type === "boolean") {
                    filtersAux[`${elem.filterMatch}`] = filtersToUse[elem.nameId].value
                } else if (elem.type === "date") {
                    filtersAux = SearchService.evaluateDateFilter(elem, filtersToUse)
                } else if (elem.type === "number") {
                    filtersAux = SearchService.evaluateNumberFilter(elem, filtersToUse)
                } else if (elem.type === "text") {
                    if (elem.subtype === "nested" && (op === "isNot" || op === "doesNotContain")) {
                        filtersAux = SearchService.evaluateTextFilter(elem, elem.filterMatchNot, filtersToUse)
                    } else {
                        filtersAux = SearchService.evaluateTextFilter(elem, elem.filterMatch, filtersToUse)
                    }
                }

                if (elem.subtype === "nested" && Object.keys(filtersAux).length !== 0) {
                    if (op !== "isNotOneOf" && op !== "isNot" && op !== "doesNotContain") {
                        const path = "{nested};" + (elem.filterMatch.split(".")[0])
                        if (filters[path] !== undefined) {
                            filters[path][Object.keys(filtersAux)[0]] = filtersAux[Object.keys(filtersAux)[0]]
                        } else {
                            filters[path] = filtersAux
                        }
                    } else {
                        filters[Object.keys(filtersAux)[0]] = filtersAux[Object.keys(filtersAux)[0]]
                    }
                }

                if (Object.keys(filtersAux).length !== 0 && elem.subtype === undefined) {
                    filters[Object.keys(filtersAux)[0]] = filtersAux[Object.keys(filtersAux)[0]]
                }
            }
        })

        if (typeOfSearch === "orders") {
            const customStatusInfo = filtersApplied.filter(elem => elem.elementSubtype === "custom-status")
            let i = 0
            const container = {}

            customStatusInfo.forEach(elem => {
                const aux = {}
                if (elem.nameId in filtersToUse) {
                    const date = SearchService.evaluateDateFilter(elem, filtersToUse)

                    aux[customDateFilterMatchId] = elem.id
                    aux[Object.keys(date)[0]] = date[Object.keys(date)[0]]
                    container[i + "{nested};" + customDatePath] = aux
                    i = i + 1
                }
            })

            if (Object.keys(container).length > 0) {
                filters["{and};"] = container
            }
        }

        if (typeOfSearch === "quotes" ||
            typeOfSearch === "invoices" ||
            typeOfSearch === "orders" ||
            typeOfSearch === "contacts" ||
            typeOfSearch === "return" ||
            typeOfSearch === "credit-memos" ||
            typeOfSearch === "itemReportList"
        ) {
            const customValue = filtersApplied.filter(elem => elem.elementSubtype === "custom-values")
            let i = 0
            const container = {}

            customValue.forEach(elem => {
                const aux = {}
                if (elem.nameId in filtersToUse) {
                    let value = {}
                    if (elem.type === "date") {
                        value = SearchService.evaluateDateFilter(elem, filtersToUse)
                    } else if (elem.type === "text") {
                        value = SearchService.evaluateTextFilter(elem, elem.filterMatch, filtersToUse)
                    } else if (elem.type === "custom-select") {
                        value = SearchService.evaluateSelectFilter(elem, [], elem.filterMatch, filtersToUse)
                    }
                    aux[elem.filterMatchId] = elem.id
                    aux[Object.keys(value)[0]] = value[Object.keys(value)[0]]
                    container[i + "{nested};" + elem.filterPath] = aux
                    i = i + 1
                }
            })

            if (Object.keys(container).length > 0) {
                filters["0{and};"] = container
            }
        }

        if (filtersToUse && filtersToUse["locationId"] !== undefined) {
            delete filters["{oneOf}"]
        }

        if (typeOfSearch == "products") {
            filters["type"] = productType || ["single"]
        }

        return {
            keywords: searchKeywords ? GlobalHelper.formatSearchKeywords(searchKeywords) : "*",
            objects: object,
            fields: fields,
            totals: totals,
            filters: filters,
            lightResult: true,
        }
    }

    static getDefaultFilters(typeOfSearch) {
        let filters = {}

        if (typeOfSearch === "invoices") {
            filters = {
                billingType: "one-time",
            }
        }
        if (typeOfSearch === "subscription") {
            filters = {
                billingType: "recurring",
            }
        }
        if (typeOfSearch === "return") {
            filters = {
                billingType: "return",
            }
        }

        if (!GlobalHelper.isAdminListing() && !["contacts", "products", "externalOrders"].includes(typeOfSearch)) {
            filters["{oneOf}"] = {
                sellerId: AppContext.getUserLoggedIn().id,
                "locationId{oneOf};": [AppContext.getUserLoggedIn().attributes.locationId, ""]
            }
        }

        return filters
    }

    static evaluateSelectFilter(elem, existingFilters, filterMatch, filtersToUse) {
        const filtersAux = {}
        let aux = ""
        const op = filtersToUse[elem.nameId].op
        const value = filtersToUse[elem.nameId].value

        if (op === "isNotOneOf") {
            aux = "{notOneOf};"
        }
        const temporal = (filterMatch) + aux
        if (Array.isArray(value) && value.length > 0) {
            if (Array.isArray(existingFilters[temporal]) && existingFilters[temporal].length > 0) {
                filtersAux[temporal] = existingFilters[temporal].concat(value)
                filtersAux[temporal] = GlobalHelper.unique(existingFilters[temporal])
            } else {
                filtersAux[temporal] = value
            }
        }
        if (!Array.isArray(value)) {
            if (!Array.isArray(existingFilters[temporal])) {
                filtersAux[temporal] = value
            }
        }
        return filtersAux
    }

    static evaluateDateFilter(elem, filtersToUse) {
        const filtersAux = {}
        const op = filtersToUse[elem.nameId].op
        const value = filtersToUse[elem.nameId].value
        const startDate = value.startDate !== "now" ? new Date(value.startDate) : new Date()
        const endDate = new Date(value.endDate)

        if (op === "greaterThan") {
            filtersAux[`${elem.filterMatch}`] = ">;" + SearchService.getFormattedDate(startDate.setHours(23, 59, 59, 999))
        } else if (op === "lowerThan") {
            filtersAux[`${elem.filterMatch}`] = "<;" + SearchService.getFormattedDate(startDate.setHours(0, 0, 0, 0))
        } else if (op === "between") {
            filtersAux[`${elem.filterMatch}`] = SearchService.getFormattedDate(
                startDate.setHours(0, 0, 0, 0)) + "<->" + SearchService.getFormattedDate(endDate.setHours(23, 59, 59, 999))
        } else if (op === "lowerOrEqualThan") {
            filtersAux[`${elem.filterMatch}`] = ">=;" + SearchService.getFormattedDate(startDate.setHours(0, 0, 0, 0))
        } else if (op === "greaterOrEqualThan") {
            filtersAux[`${elem.filterMatch}`] = "<=;" + SearchService.getFormattedDate(startDate.setHours(23, 59, 59, 999))
        } else if (op === "within") {
            filtersAux[`${elem.filterMatch}`] = "now-" + value.endDate + value.startDate + "/d" + "<->" + "now/d"
        } else if (op === "is") {
            const dateStart = this.getFormattedDate(moment(startDate.setHours(23, 59, 59, 999)).subtract(1, "days"))
            const dateEnd = this.getFormattedDate(moment(startDate.setHours(23, 59, 59, 999)))
            filtersAux[`${elem.filterMatch}`] = dateStart + "<->" + dateEnd
        }

        return filtersAux
    }

    static getFormattedDate(date) {
        return (moment(date)).format()
    }

    static evaluateNumberFilter(elem, filtersToUse) {
        const filtersAux = {}
        const value = filtersToUse[elem.nameId].value
        const op = filtersToUse[elem.nameId].op

        if (op === "greaterThan") {
            filtersAux[`${elem.filterMatch}`] = ">;" + value
        } else if (op === "lowerThan") {
            filtersAux[`${elem.filterMatch}`] = "<;" + value
        } else if (op === "lowerOrEqualThan") {
            filtersAux[`${elem.filterMatch}`] = "<=;" + value
        } else if (op === "greaterOrEqualThan") {
            filtersAux[`${elem.filterMatch}`] = ">=;" + value
        } else if (op === "is") {
            const numberStart = parseFloat(value) - 0.01
            const numberEnd = parseFloat(value) + 0.01
            filtersAux[`${elem.filterMatch}`] = numberStart + "<->" + numberEnd
        }
        return filtersAux
    }

    static evaluateTextFilter(elem, filterMatch, filtersToUse) {
        const filtersAux = {}
        const op = filtersToUse[elem.nameId].op
        const value = filtersToUse[elem.nameId].value

        if (op === "is") {
            filtersAux[filterMatch] = value
        } else if (op === "isNot") {
            filtersAux[filterMatch + "{notOneOf};"] = value
        } else if (op === "contains") {
            filtersAux[filterMatch] = "{text};" + "*" + value + "*"
        } else if (op === "startWith") {
            filtersAux[filterMatch] = "{text};" + value + "*"
        } else if (op === "endWith") {
            filtersAux[filterMatch] = "{text};" + "*" + value
        } else if (op === "doesNotContains") {
            filtersAux[filterMatch + "{notContainText};"] = "*" + value + "*"
        }

        return filtersAux
    }
}

const getContactSearchPostData = (
    keyword,
    activePage,
    pageSize,
    labels
) => {
    const data = {
        keywords: GlobalHelper.formatSearchKeywords(keyword),
        objects: ["contacts"],
        fields: [
            "name",
            "nameAnalyzed",
            "nameKeyword",
            "nameSort",
            "email",
        ],
        pageSize: pageSize,
        activePage: activePage,
        sortByField: "nameSort",
        sortAsc: true,
        lightResult: true,
        filters: {
            contactType: "persons",
            isArchived: false,
            labelIds: undefined,
        }
    }
    if (labels.length != 0) data.filters.labelIds = labels
    return data
}




export default SearchService


// WEBPACK FOOTER //
// ./src/state/search/SearchService.ts